/* The campus pin on fleet Settings.
 *
 * Read-only unless `onChange` is given. Until 2026-09-25 it was read-only full
 * stop — "School admins may look; they may not move it. PEAR console owns the
 * write" — and a school that moved had to ask PEAR to move its own gate. With
 * `onChange` the marker drags and a click on the map re-places it; the parent
 * decides when that becomes a save. The 150 m circle is the driver app's
 * arrival fence and follows the marker so the admin sees what the fence will
 * be, not where it was. */
import React from "react";
import { Circle, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from "../../utils/googleMapsLoader";

/** Same radius the driver app uses for school + depot (`GeofenceEngine.kCampusRadiusM`). */
export const CAMPUS_FENCE_RADIUS_M = 150;

export type SchoolLocationMapProps = {
  lat: number;
  lng: number;
  height?: number;
  /** When set, the pin can be dragged or re-placed by clicking the map. */
  onChange?: (p: { lat: number; lng: number }) => void;
};

export default function SchoolLocationMap({ lat, lng, height = 220, onChange }: SchoolLocationMapProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const center = { lat, lng };

  if (!apiKey || loadError) {
    return (
      <div
        style={{
          height,
          borderRadius: 14,
          background: "#F4F1FA",
          border: "1px solid #E9E5F3",
          display: "grid",
          placeItems: "center",
          fontSize: 13,
          color: "#8E86A8",
        }}
      >
        Map unavailable — pin is {lat.toFixed(5)}, {lng.toFixed(5)}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          height,
          borderRadius: 14,
          background: "#F4F1FA",
          border: "1px solid #E9E5F3",
        }}
      />
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height, borderRadius: 14 }}
      center={center}
      zoom={16}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        keyboardShortcuts: false,
        gestureHandling: "cooperative",
        // A crosshair says "click to place" without a word of instruction.
        draggableCursor: onChange ? "crosshair" : undefined,
      }}
      onClick={onChange ? (e) => {
        const ll = e.latLng;
        if (ll) onChange({ lat: ll.lat(), lng: ll.lng() });
      } : undefined}
    >
      <Marker
        position={center}
        clickable={false}
        draggable={!!onChange}
        onDragEnd={onChange ? (e) => {
          const ll = e.latLng;
          if (ll) onChange({ lat: ll.lat(), lng: ll.lng() });
        } : undefined}
      />
      <Circle
        center={center}
        radius={CAMPUS_FENCE_RADIUS_M}
        options={{
          strokeColor: "#6F4FF0",
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: "#6F4FF0",
          fillOpacity: 0.12,
          clickable: false,
        }}
      />
    </GoogleMap>
  );
}
